import { spawn } from 'node:child_process'
import { writeFile } from 'node:fs/promises'
import { join } from 'node:path'

import type { DaemonServerOptions, ServerStatusResult, WaitForReadyOptions } from './types.js'

/**
 * Escapes XML special characters to prevent XML injection
 */
function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/**
 * Checks if ClickHouse server is running by attempting to connect to the HTTP port
 *
 * @param options - Options for server status check
 * @returns Server status information
 */
export async function isServerRunning(
  options: { port?: number } = {},
): Promise<ServerStatusResult> {
  const port = options.port || 8123
  try {
    const response = await fetch(`http://localhost:${port}/ping`, {
      method: 'GET',
      signal: AbortSignal.timeout(2000),
    })

    if (response.ok) {
      const text = await response.text()
      return {
        isRunning: text === 'Ok.\n',
        port,
      }
    }

    return {
      isRunning: false,
    }
  } catch (error) {
    return {
      isRunning: false,
    }
  }
}

/**
 * Waits for ClickHouse server to become ready by polling the HTTP port
 *
 * @param options - Options for waiting
 * @returns True if server became ready, throws if timeout exceeded
 */
export async function waitForReady(options: WaitForReadyOptions = {}): Promise<boolean> {
  const port = options.port || 8123
  const timeout = options.timeout || 30000
  const interval = options.interval || 500

  const startTime = Date.now()

  while (Date.now() - startTime < timeout) {
    const status = await isServerRunning({ port })

    if (status.isRunning) {
      return true
    }

    await new Promise((resolve) => setTimeout(resolve, interval))
  }

  throw new Error(`ClickHouse server did not become ready within ${timeout}ms`)
}

/**
 * Generates a minimal ClickHouse server configuration file
 *
 * @param options - Server options
 * @returns Path to the generated config file
 */
async function generateConfigFile(options: DaemonServerOptions): Promise<string> {
  const httpPort = options.httpPort || 8123
  const tcpPort = options.tcpPort || 9000
  const configPath = join(options.dataDir, 'config.xml')

  // Escape all user-provided paths to prevent XML injection
  const logDir = escapeXml(options.logDir)
  const dataDir = escapeXml(options.dataDir)

  const config = `<?xml version="1.0"?>
<clickhouse>
    <logger>
        <level>information</level>
        <log>${logDir}/clickhouse-server.log</log>
        <errorlog>${logDir}/clickhouse-server.err.log</errorlog>
        <size>1000M</size>
        <count>10</count>
    </logger>

    <http_port>${httpPort}</http_port>
    <tcp_port>${tcpPort}</tcp_port>

    <listen_host>127.0.0.1</listen_host>

    <path>${dataDir}/</path>
    <tmp_path>${dataDir}/tmp/</tmp_path>
    <user_files_path>${dataDir}/user_files/</user_files_path>
    <format_schema_path>${dataDir}/format_schemas/</format_schema_path>

    <users>
        <default>
            <password></password>
            <networks>
                <ip>127.0.0.1</ip>
            </networks>
            <profile>default</profile>
            <quota>default</quota>
        </default>
    </users>

    <profiles>
        <default>
            <max_memory_usage>10000000000</max_memory_usage>
            <use_uncompressed_cache>0</use_uncompressed_cache>
            <load_balancing>random</load_balancing>
        </default>
    </profiles>

    <quotas>
        <default>
            <interval>
                <duration>3600</duration>
                <queries>0</queries>
                <errors>0</errors>
                <result_rows>0</result_rows>
                <read_rows>0</read_rows>
                <execution_time>0</execution_time>
            </interval>
        </default>
    </quotas>
</clickhouse>
`

  await writeFile(configPath, config, 'utf-8')
  return configPath
}

/**
 * Starts ClickHouse server as a daemon process
 *
 * @param options - Server startup options
 * @returns Process ID of the started server
 */
export async function startServer(options: DaemonServerOptions): Promise<number> {
  // Check if already running
  const httpPort = options.httpPort || 8123
  const status = await isServerRunning({ port: httpPort })

  if (status.isRunning) {
    throw new Error(`ClickHouse server is already running on port ${httpPort}`)
  }

  // Generate configuration file
  const configPath = await generateConfigFile(options)

  // Start server as daemon
  const serverProcess = spawn(
    options.binaryPath,
    ['server', '--config-file', configPath, '--daemon'],
    {
      detached: true,
      stdio: 'ignore',
    },
  )

  // Detach from parent process so it can continue running
  serverProcess.unref()

  // Wait for server to become ready
  try {
    await waitForReady({
      interval: 500,
      port: httpPort,
      timeout: 30000,
    })
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to start ClickHouse server: ${error.message}`)
    }
    throw new Error('Failed to start ClickHouse server: Unknown error')
  }

  return serverProcess.pid || -1
}

/**
 * Attempts to gracefully stop the ClickHouse server
 *
 * @param options - Options for stopping the server
 * @returns True if server was stopped successfully
 */
export async function stopServer(options: { port?: number } = {}): Promise<boolean> {
  const port = options.port || 8123
  const status = await isServerRunning({ port })

  if (!status.isRunning) {
    return true // Already stopped
  }

  try {
    // Send shutdown command to server
    const response = await fetch(`http://localhost:${port}/?query=SYSTEM%20SHUTDOWN`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      return false
    }

    // Wait for server to stop (up to 10 seconds)
    const stopTimeout = 10000
    const startTime = Date.now()

    while (Date.now() - startTime < stopTimeout) {
      const currentStatus = await isServerRunning({ port })
      if (!currentStatus.isRunning) {
        return true
      }
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    return false
  } catch (error) {
    return false
  }
}
